// Format a forecast probability as a display-percent STRING (no "%" — callers
// append it). Whole percent in the normal range, but ONE decimal in the extreme
// tails (below 1% or above 99%) so a very-unlikely / near-certain forecast reads
// as "0.6" / "99.7" instead of collapsing to a bare "0" / "1" / "99" / "100" and
// hiding how extreme it is. The backend sends full float precision; we only round
// for display, so the sub-percent detail is real (not fabricated).
//
// Input is a fraction in [0,1] (the wire shape of contract.probability). Returns
// "0" for null/NaN/≤0 and "100" for ≥100% — only exact certainty reads 0 or 100.
// A tail too small for one decimal reads "<0.1" / ">99.9", never "0" / "100".
// Binary and multiway displays share this one rule.
export function fmtPct(p) {
  const frac = typeof p === "number" && Number.isFinite(p) ? p : 0;
  const pct = frac * 100;
  if (pct <= 0) return "0";
  if (pct >= 100) return "100";
  if (pct >= 1 && pct <= 99) return String(Math.round(pct));
  const d = Math.round(pct * 10) / 10;
  if (d <= 0) return "<0.1";
  if (d >= 100) return ">99.9";
  return String(d);
}

// A yes/no forecast's answer names its larger side (Yes at exactly 50%), so every display of it
// reads the same way: "97% chance of No", never a bare "3%".

/**
 * The larger side of a yes/no forecast and that side's chance.
 * @param {number} pYes P(Yes), 0–1
 * @returns {{label: "Yes"|"No", p: number}}
 */
export function leadingSide(pYes) {
  return pYes >= 0.5 ? { label: "Yes", p: pYes } : { label: "No", p: 1 - pYes };
}

/**
 * "97% chance of No" for P(Yes) 0.03.
 * @param {number} pYes P(Yes), 0–1
 * @returns {string}
 */
export function sideChanceText(pYes) {
  const side = leadingSide(pYes);
  return `${fmtPct(side.p)}% chance of ${side.label}`;
}
